import React, { useState, useEffect } from 'react';
import { Volume2, VolumeX, Pause, Play, SkipForward, Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const VoiceAssistant = ({ text, language = 'hindi', onComplete }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [speed, setSpeed] = useState(1.0);
  const [showSettings, setShowSettings] = useState(false);
  const [utterance, setUtterance] = useState(null);
  const [isSupported, setIsSupported] = useState(true);

  // Language codes for Telugu and Hindi
  const languageCodes = {
    telugu: 'te-IN',
    hindi: 'hi-IN'
  };

  useEffect(() => {
    // Check if browser supports speech synthesis
    if (!('speechSynthesis' in window)) {
      setIsSupported(false);
      toast.error('Voice assistant not supported in this browser');
      return;
    }

    // Create utterance when text changes
    if (text) {
      const newUtterance = new SpeechSynthesisUtterance(text);
      newUtterance.lang = languageCodes[language] || languageCodes.hindi;
      newUtterance.rate = speed;
      newUtterance.pitch = 1.0;
      newUtterance.volume = 1.0;

      newUtterance.onend = () => {
        setIsPlaying(false);
        setIsPaused(false);
        if (onComplete) onComplete();
      };

      newUtterance.onerror = (event) => {
        console.error('Speech error:', event);
        setIsPlaying(false);
        setIsPaused(false);
        toast.error('Voice playback error');
      };

      setUtterance(newUtterance);
    }

    return () => {
      if (window.speechSynthesis.speaking) {
        window.speechSynthesis.cancel();
      }
    };
  }, [text, language, speed]);

  const handlePlay = () => {
    if (!utterance) return;

    if (isPaused) {
      window.speechSynthesis.resume();
      setIsPaused(false);
      setIsPlaying(true);
    } else {
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(utterance);
      setIsPlaying(true);
      setIsPaused(false);
    }
  };

  const handlePause = () => {
    if (window.speechSynthesis.speaking) {
      window.speechSynthesis.pause();
      setIsPaused(true);
      setIsPlaying(false);
    }
  };

  const handleStop = () => {
    window.speechSynthesis.cancel();
    setIsPlaying(false);
    setIsPaused(false);
  };

  const handleSpeedChange = (newSpeed) => {
    setSpeed(newSpeed);
    if (isPlaying) {
      window.speechSynthesis.cancel();
      setIsPlaying(false);
    }
  };

  if (!isSupported) {
    return null;
  }

  return (
    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200 p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Volume2 className="w-5 h-5 text-blue-900" />
          <div>
            <p className="text-sm font-medium text-slate-900">
              {language === 'telugu' ? 'వాయిస్ అసిస్టెంట్ (Telugu)' : 'वॉइस असिस्टेंट (Hindi)'}
            </p>
            <p className="text-xs text-slate-600">
              {language === 'telugu' ? 'సమాధానం వినండి' : 'उत्तर सुनें'}
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          {/* Speed Control */}
          <div className="relative">
            <Button
              onClick={() => setShowSettings(!showSettings)}
              variant="outline"
              size="sm"
              className="border-blue-200"
              data-testid="voice-settings-btn"
            >
              <Settings className="w-4 h-4" />
            </Button>
            
            {showSettings && (
              <div className="absolute right-0 top-full mt-2 bg-white rounded-lg shadow-lg border border-slate-200 p-3 z-10 w-48">
                <p className="text-xs font-medium text-slate-700 mb-2">
                  {language === 'telugu' ? 'వేగం' : 'गति'}
                </p>
                <div className="space-y-1">
                  {[0.75, 1.0, 1.25, 1.5].map((s) => (
                    <button
                      key={s}
                      onClick={() => handleSpeedChange(s)}
                      className={`w-full text-left px-3 py-2 rounded text-sm ${
                        speed === s
                          ? 'bg-blue-900 text-white'
                          : 'hover:bg-slate-50 text-slate-700'
                      }`}
                      data-testid={`voice-speed-${s}x`}
                    >
                      {s}x {s === 1.0 ? '(Normal)' : s < 1.0 ? '(Slow)' : '(Fast)'}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Play/Pause Controls */}
          {!isPlaying && !isPaused && (
            <Button
              onClick={handlePlay}
              className="bg-blue-900 hover:bg-blue-800 text-white"
              size="sm"
              data-testid="voice-play-btn"
            >
              <Play className="w-4 h-4 mr-1" />
              {language === 'telugu' ? 'ప్లే' : 'प्ले'}
            </Button>
          )}

          {isPlaying && (
            <Button
              onClick={handlePause}
              className="bg-yellow-600 hover:bg-yellow-700 text-white"
              size="sm"
              data-testid="voice-pause-btn"
            >
              <Pause className="w-4 h-4 mr-1" />
              {language === 'telugu' ? 'పాజ్' : 'रोकें'}
            </Button>
          )}

          {(isPlaying || isPaused) && (
            <Button
              onClick={handleStop}
              variant="outline"
              size="sm"
              className="border-red-200 text-red-600 hover:bg-red-50"
              data-testid="voice-stop-btn"
            >
              <VolumeX className="w-4 h-4 mr-1" />
              {language === 'telugu' ? 'ఆపు' : 'बंद'}
            </Button>
          )}
        </div>
      </div>

      {/* Progress Indicator */}
      {isPlaying && (
        <div className="mt-3">
          <div className="h-1 bg-blue-200 rounded-full overflow-hidden">
            <div className="h-full bg-blue-900 animate-pulse" style={{ width: '100%' }} />
          </div>
        </div>
      )}
    </div>
  );
};

export default VoiceAssistant;
